import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Clock, AlertTriangle, Users, ChevronDown, ChevronUp, Calendar, X } from 'lucide-react';

const fmt = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

const LeadPipeline = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [expandedSalesman, setExpandedSalesman] = useState(null);

  // Input state (live editing — does NOT trigger fetch on change)
  const [fromDateInput, setFromDateInput] = useState('');
  const [toDateInput, setToDateInput] = useState('');

  // Applied state (what is actually sent to the API)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [showDateFilter, setShowDateFilter] = useState(false);

  // ── In-memory cache with TTL ──
  const CACHE_TTL = 60_000; // 60 seconds
  const cache = useRef({ data: null, timestamp: 0, filters: { fromDate: '', toDate: '' } });

  const invalidateCache = () => {
    cache.current.timestamp = 0;
  };

  const fetchPipeline = async (force = false) => {
    const now = Date.now();
    const age = now - cache.current.timestamp;
    const hasCached = cache.current.data !== null;
    const isFresh = age < CACHE_TTL;
    const filtersMatch = cache.current.filters.fromDate === fromDate &&
                        cache.current.filters.toDate === toDate;

    // ── Cache hit: instant, no network, no spinner ──
    if (!force && hasCached && isFresh && filtersMatch) {
      setData(cache.current.data);
      setLoading(false);
      return;
    }

    // First load → full spinner. Re-load → subtle bg indicator, keep stale data visible.
    if (!hasCached || !filtersMatch) {
      setLoading(true);
    } else {
      setBgRefreshing(true);
    }

    try {
      const params = new URLSearchParams();
      // CRITICAL FIX: Only add date params if they have actual values
      if (fromDate && fromDate.trim()) params.set('from_date', fromDate);
      if (toDate && toDate.trim()) params.set('to_date', toDate);
      const url = `/crm/pipeline${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await api.get(url);
      cache.current = {
        data: res.data,
        timestamp: Date.now(),
        filters: { fromDate, toDate }
      };
      setData(res.data);
    } catch (e) {
      console.error(e);
      // Keep stale data visible on network failure
      if (hasCached && filtersMatch) setData(cache.current.data);
    } finally {
      setLoading(false);
      setBgRefreshing(false);
    }
  };

  // Refetch every time this page is navigated to (including browser back)
  // Show cached data immediately if available
  useEffect(() => {
    const filtersMatch = cache.current.filters.fromDate === fromDate &&
                        cache.current.filters.toDate === toDate;
    if (cache.current.data !== null && filtersMatch) {
      setData(cache.current.data);
      setLoading(false);
      return; // Don't fetch if cache is valid - prevents unnecessary fetches
    }
    fetchPipeline();
  }, [location.key, fromDate, toDate]);

  // Re-fetch when user returns to the tab (handles iOS background/foreground transitions)
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        invalidateCache();
        fetchPipeline(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fromDate, toDate]);

  const toggleSalesman = (id) => {
    setExpandedSalesman(prev => prev === id ? null : id);
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  const stats = data?.stats || {};
  const pipeline = data?.pipeline || [];
  const conversionRate = (stats.total_won + stats.total_lost) > 0
    ? Math.round((stats.total_won / (stats.total_won + stats.total_lost)) * 100) : 0;

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="lead-pipeline-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                Lead Pipeline
                {bgRefreshing && <RefreshCw className="w-3 h-3 animate-spin opacity-70" />}
              </h1>
              <p className="text-xs text-emerald-200">Active leads by salesman</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className={`text-white hover:bg-emerald-700 ${(fromDate || toDate) ? 'bg-emerald-600' : ''}`}
              onClick={() => {
                setShowDateFilter(p => {
                  // When opening: sync inputs to current applied state
                  if (!p) { setFromDateInput(fromDate); setToDateInput(toDate); }
                  return !p;
                });
              }}
              title="Date filter"
            >
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => fetchPipeline(true)}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Date Filter Panel */}
        {showDateFilter && (
          <div className="mt-3 bg-emerald-600 rounded-lg p-3 space-y-2" data-testid="date-filter">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-semibold text-white uppercase tracking-wide block mb-1">From Date</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY-MM-DD"
                  value={fromDateInput}
                  onChange={(e) => setFromDateInput(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-emerald-400 rounded bg-white text-gray-900 font-semibold tracking-wide"
                  data-testid="from-date-input"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-white uppercase tracking-wide block mb-1">To Date</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY-MM-DD"
                  value={toDateInput}
                  onChange={(e) => setToDateInput(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-emerald-400 rounded bg-white text-gray-900 font-semibold tracking-wide"
                  data-testid="to-date-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 bg-white text-emerald-700 hover:bg-emerald-50 text-xs font-semibold h-8"
                onClick={() => { setFromDate(fromDateInput); setToDate(toDateInput); }}
                data-testid="apply-filter-btn"
              >
                Apply
              </Button>
              {(fromDate || toDate || fromDateInput || toDateInput) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-emerald-700 text-xs h-8 px-3"
                  onClick={() => { setFromDateInput(''); setToDateInput(''); setFromDate(''); setToDate(''); }}
                  data-testid="clear-filter-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Stats Cards */}
        <div className="px-4 pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pipeline-stats">
            <div className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/lead-sessions')}>
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Pipeline Value</p>
              <p className="text-xl font-bold text-emerald-700 mt-1">{fmt(stats.total_pipeline_value)}</p>
              <p className="text-xs text-gray-400">{stats.total_open + stats.total_parked} active leads</p>
            </div>
            <div className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/lead-sessions?lead_status=won')}>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-500" />
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Won</p>
              </div>
              <p className="text-xl font-bold text-green-600 mt-1">{fmt(stats.won_value)}</p>
              <p className="text-xs text-gray-400">{stats.total_won} leads | {conversionRate}% rate</p>
            </div>
            <div className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/loss-reason-analytics')}>
              <div className="flex items-center gap-1">
                <TrendingDown className="w-3 h-3 text-red-500" />
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Lost</p>
              </div>
              <p className="text-xl font-bold text-red-500 mt-1">{fmt(stats.lost_value)}</p>
              <p className="text-xs text-gray-400">{stats.total_lost} leads</p>
            </div>
            <div className="bg-white rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/overdue-followups')}>
              <div className="flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-500" />
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">Follow-ups</p>
              </div>
              <p className="text-xl font-bold text-amber-600 mt-1">{stats.overdue_followups}</p>
              <p className="text-xs text-gray-400">overdue | {stats.total_parked} parked</p>
            </div>
          </div>
        </div>

        {/* Pipeline Value Bar */}
        {stats.total_pipeline_value > 0 && (
          <div className="px-4 pt-3">
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 font-medium mb-2">Pipeline by Salesman</p>
              <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
                {pipeline.map((s, i) => {
                  const pct = (s.total_value / stats.total_pipeline_value) * 100;
                  if (pct < 1) return null;
                  const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];
                  return (
                    <div key={s.salesman_id} className={`${colors[i % colors.length]} transition-all`}
                      style={{ width: `${pct}%` }} title={`${s.salesman_name}: ${fmt(s.total_value)}`} />
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {pipeline.map((s, i) => {
                  const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];
                  return (
                    <span key={s.salesman_id} className="flex items-center gap-1 text-xs text-gray-600">
                      <span className={`w-2 h-2 rounded-full ${colors[i % colors.length]}`} />
                      {s.salesman_name}: {fmt(s.total_value)}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Salesman Cards */}
        <div className="px-4 pt-3 pb-4 space-y-3" data-testid="pipeline-list">
          {pipeline.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="font-medium">No active leads in pipeline</p>
              <p className="text-sm mt-1">Create lead sessions to start tracking</p>
            </div>
          ) : pipeline.map(salesman => (
            <div key={salesman.salesman_id} className="bg-white rounded-lg border overflow-hidden" data-testid={`pipeline-salesman-${salesman.salesman_id}`}>
              {/* Salesman Header */}
              <button
                onClick={() => toggleSalesman(salesman.salesman_id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-sm text-gray-900">{salesman.salesman_name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{salesman.leads.length} leads</span>
                      {salesman.open_count > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">{salesman.open_count} open</span>}
                      {salesman.parked_count > 0 && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">{salesman.parked_count} parked</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-emerald-700">{fmt(salesman.total_value)}</span>
                  {expandedSalesman === salesman.salesman_id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>

              {/* Expanded Leads */}
              {expandedSalesman === salesman.salesman_id && (
                <div className="border-t divide-y" data-testid={`leads-list-${salesman.salesman_id}`}>
                  {salesman.leads.map(lead => {
                    const todayStr = new Date().toISOString().split('T')[0];
                    const isOverdue = lead.status === 'parked' && lead.follow_up_date && lead.follow_up_date < todayStr;
                    const isDueToday = lead.status === 'parked' && lead.follow_up_date && lead.follow_up_date === todayStr;
                    return (
                      <div
                        key={lead._id}
                        className={`px-4 py-3 hover:bg-gray-50 cursor-pointer ${isOverdue ? 'bg-red-50/50' : ''}`}
                        onClick={() => navigate(`/lead-sessions/${lead.session_id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-gray-900">{lead.product_name}</span>
                              {lead.main_group && <span className="text-xs text-gray-500">{lead.main_group}</span>}
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${
                                lead.status === 'open' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-amber-100 text-amber-700 border-amber-200'
                              }`}>{lead.status}</span>
                              {isOverdue && (
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">OVERDUE</span>
                              )}
                              {isDueToday && (
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">TODAY</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {lead.client_name} | {lead.session_number}
                              {lead.occasion && ` | ${lead.occasion}`}
                            </p>
                            {lead.follow_up_date && (
                              <p className={`text-xs mt-0.5 flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : isDueToday ? 'text-amber-600 font-semibold' : 'text-amber-600'}`}>
                                <Clock className="w-3 h-3" />
                                Follow-up: {new Date(lead.follow_up_date + 'T00:00:00').toLocaleDateString('en-IN')}
                              </p>
                            )}
                          </div>
                          <span className="font-bold text-sm text-emerald-700 flex-shrink-0 ml-3">{fmt(lead.estimated_value)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadPipeline;
